import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Globe, Tag } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('client');
  const { login } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('success'));
      
      if (user.role === 'admin') {
        navigate('/admin-pin');
      } else if (user.role === 'trainer') {
        navigate('/trainer');
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const LegalFooterLinks = () => (
    <div className="mt-6 text-center">
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-zinc-500">
        <Link to="/legal/aviso-legal" className="hover:text-primary transition-colors">
          Aviso Legal
        </Link>
        <span className="text-zinc-700">|</span>
        <Link to="/pricing" className="hover:text-primary transition-colors font-semibold text-primary/80" data-testid="footer-link-pricing">
          Planes y precios
        </Link>
        <span className="text-zinc-700">|</span>
        <Link to="/legal/privacidad" className="hover:text-primary transition-colors">
          Privacidad
        </Link>
        <span className="text-zinc-700">|</span>
        <Link to="/legal/cookies" className="hover:text-primary transition-colors">
          Cookies
        </Link>
        <span className="text-zinc-700">|</span>
        <Link to="/legal/terminos-uso" className="hover:text-primary transition-colors">
          Términos
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col lg:flex-row">
      {/* Left side - Hero section with community collage */}
      <div className="relative lg:w-3/5 min-h-[50vh] lg:min-h-screen overflow-hidden">
        {/* Image collage */}
        <div className="absolute inset-0">
          {/* Pareja senior con entrenador personal (top left, large) */}
          <div className="absolute top-0 left-0 w-[55%] h-[50%] p-1">
            <img 
              src="https://media.gettyimages.com/id/1436141007/es/foto/pareja-senior-haciendo-ejercicios-de-estiramiento-con-entrenador-personal-en-el-gimnasio.jpg?s=612x612&w=0&k=20&c=uLmiUwBFnp5e0wIX8n2fOxbqhKQ9XKps9o48s-IB3Yk="
              alt="Pareja senior con entrenador personal"
              className="w-full h-full object-cover rounded-sm opacity-85"
            />
          </div>
          
          {/* Abuelos jugando béisbol con nietos (top right) */}
          <div className="absolute top-0 right-0 w-[45%] h-[35%] p-1">
            <img 
              src="https://st.depositphotos.com/1037987/5069/i/450/depositphotos_50695655-stock-photo-grandparents-playing-baseball-with-grandchildren.jpg"
              alt="Abuelos jugando con nietos"
              className="w-full h-full object-cover rounded-sm opacity-85"
            />
          </div>
          
          {/* Grupo diverso mujeres gimnasio (middle right) */}
          <div className="absolute top-[35%] right-0 w-[45%] h-[35%] p-1">
            <img 
              src="https://us.123rf.com/450wm/dragonimages/dragonimages2111/dragonimages211100938/177814779-grupo-diverso-de-mujeres-haciendo-ejercicios-frente-a-un-gran-espejo-en-el-gimnasio.jpg?ver=6"
              alt="Grupo diverso de mujeres en gimnasio"
              className="w-full h-full object-cover rounded-sm opacity-85"
            />
          </div>
          
          {/* Personas maduras en parque (bottom left) */}
          <div className="absolute bottom-0 left-0 w-[50%] h-[50%] p-1">
            <img 
              src="https://img.freepik.com/fotos-premium/personas-maduras-hacen-ejercicio-parque_256588-303.jpg?semt=ais_hybrid&w=740&q=80"
              alt="Personas maduras ejercitándose en parque"
              className="w-full h-full object-cover rounded-sm opacity-85"
            />
          </div>
          
          {/* Pareja joven atlética (bottom right) */}
          <div className="absolute bottom-0 right-0 w-[50%] h-[30%] p-1">
            <img 
              src="https://t4.ftcdn.net/jpg/04/20/68/11/360_F_420681176_7pIBXB54hnIuHd0I6O7y3dKh5VmBNorL.jpg"
              alt="Pareja atlética"
              className="w-full h-full object-cover rounded-sm opacity-85"
            />
          </div>
        </div>
        
        {/* Gradient overlays - stronger on bottom left for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        
        {/* Hero content - Centrado abajo */}
        <div className="absolute bottom-6 lg:bottom-10 left-0 right-0 z-10 px-6 lg:px-10 text-center lg:text-center lg:right-auto lg:left-0 lg:max-w-[60%]">
          <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-primary mb-2 leading-snug tracking-wide uppercase drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
            SALUD ESTRUCTURADA PARA TODOS
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-white font-semibold mb-2 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
            Cuerpo, mente y prop{'\u00f3'}sito
          </p>
          <p className="text-sm lg:text-base text-primary/80 mb-1 tracking-wide drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
            Movimiento con sentido
          </p>
          <p className="text-sm lg:text-base text-white/70 tracking-wide drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
            Ordena, mide y sostiene
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="lg:w-2/5 flex items-center justify-center p-6 lg:p-12 relative bg-[#050505]">
        {/* Language toggle */}
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
            className="text-muted-foreground hover:text-foreground"
            data-testid="language-toggle"
          >
            <Globe className="h-4 w-4 mr-2" />
            {language === 'es' ? 'EN' : 'ES'}
          </Button>
        </div>

        <Card className="w-full max-w-md bg-zinc-900/50 backdrop-blur-sm border-zinc-800/50 shadow-2xl">
          <CardContent className="p-8">
            {/* Mobile logo (hidden on desktop) */}
            <div className="lg:hidden flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
                alt="TRINES PORTAL 33 - FIT GUARDIAN" 
                className="h-16"
                data-testid="login-logo-mobile"
              />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">{t('welcomeBack')}</h2>
              <p className="text-muted-foreground mt-2">{t('enterCredentials')}</p>
            </div>

            {/* Botón GRANDE pricing — arriba del todo, primer impacto visual */}
            <Link
              to="/pricing"
              className="block mb-6 group"
              data-testid="login-link-pricing"
            >
              <div className="rounded-xl border-2 border-primary/50 bg-gradient-to-r from-primary/20 via-amber-500/15 to-primary/20 hover:border-primary transition-all p-4 flex items-center gap-3 shadow-lg shadow-primary/10">
                <div className="h-11 w-11 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0">
                  <Tag className="h-5 w-5 text-primary" strokeWidth={2.5} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-bold text-foreground">Ver planes y precios</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Free, Pro, Familia, Entrenadores · 14-30 días gratis</p>
                </div>
                <span className="text-primary text-2xl font-bold">→</span>
              </div>
            </Link>

            {/* Role Selector */}
            <div className="flex gap-2 mb-6" data-testid="role-selector">
              <button
                type="button"
                onClick={() => setSelectedRole('client')}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all border ${
                  selectedRole === 'client'
                    ? 'bg-primary/15 border-primary text-primary'
                    : 'bg-zinc-800/30 border-zinc-700/50 text-zinc-400 hover:border-zinc-600'
                }`}
                data-testid="role-client-btn"
              >
                Soy Cliente
              </button>
              <button
                type="button"
                onClick={() => setSelectedRole('trainer')}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all border ${
                  selectedRole === 'trainer'
                    ? 'bg-blue-500/15 border-blue-500 text-blue-400'
                    : 'bg-zinc-800/30 border-zinc-700/50 text-zinc-400 hover:border-zinc-600'
                }`}
                data-testid="role-trainer-btn"
              >
                Soy Entrenador
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  required
                  className="bg-zinc-800/50 border-zinc-700 focus:border-primary h-12"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-foreground">{t('password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
                    data-testid="forgot-password-link"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-zinc-800/50 border-zinc-700 focus:border-primary h-12"
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wide transition-all hover:shadow-lg hover:shadow-primary/25"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('login')
                )}
              </Button>
            </form>

            {/* Registro público — auto-onboarding */}
            <div className="mt-5 text-center border-t border-zinc-800 pt-4">
              <p className="text-sm text-muted-foreground mb-2">¿Todavía no tienes cuenta?</p>
              <Link
                to="/register"
                data-testid="login-register-link"
                className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-primary text-foreground font-semibold text-sm transition-all"
              >
                Crear cuenta nueva
                <span className="text-primary">→</span>
              </Link>
            </div>

            <LegalFooterLinks />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
